<script setup>
import { Link, router } from '@inertiajs/vue3'
import { Container, Pagination } from '@codinglabsau/ui'
import { Button } from '@codinglabsau/gooey'
import { XIcon } from 'lucide-vue-next'
import moment from 'moment'
import ClaimCard from '@/Components/Claims/ClaimCard.vue'
import LevelProgression from '@/Components/Users/LevelProgression.vue'
import SubSection from '@/Components/SubSection.vue'
import UserAbout from '@/Components/Users/UserAbout.vue'
import UserAchievement from '@/Components/Users/UserAchievement.vue'

defineProps({
  user: Object,
  claims: Object,
  achievementsCount: Number,
  unlockedAchievementsCount: Number,
  achievements: Object,
})

const disclaimConvict = (claim) => {
  router.delete(route('convicts.disclaim', claim.id))
}
</script>

<template>
  <Container>
    <div class="grid grid-cols-1 gap-y-4 lg:grid-cols-3 lg:gap-x-4">
      <div class="col-span-1 w-full">
        <div class="sticky top-2 grid gap-4 md:grid-cols-2 lg:grid-cols-1">
          <slot name="left-column">
            <UserAbout :user="user">
              <template #actions>
                <slot name="user-actions" />
              </template>
            </UserAbout>

            <div class="rounded-md bg-white p-4 shadow">
              <LevelProgression :user="user" />
            </div>
          </slot>
        </div>
      </div>

      <div class="col-span-2 space-y-4 rounded-md bg-white p-4 shadow">
        <slot name="main-column">
          <SubSection v-if="claims.data.length > 0" title="Claims">
            <div class="grid gap-8">
              <div v-for="claim in claims.data" :key="claim.id" class="space-y-2">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-1 text-sm">
                    <time datetime="claim.created_at" class="text-gray-500">
                      {{ moment(claim.created_at).format('MMM D, YYYY') }}
                    </time>
                    -
                    <Link
                      :href="route('convicts.show', claim.meta.convict_slug)"
                      class="flex items-center gap-2 font-medium hover:underline"
                    >
                      {{ claim.meta.convict_name }}
                    </Link>
                  </div>

                  <div class="text-sm">
                    <slot name="claim-actions" :claim="claim">
                      <Button
                        v-if="claim.user_id === $page.props.auth.user.id"
                        variant="outline"
                        @click="disclaimConvict(claim)"
                      >
                        <XIcon class="mr-2 size-4" />
                        Disclaim
                      </Button>
                    </slot>
                  </div>
                </div>

                <ClaimCard :claim="claim" :with-actions="false" />
              </div>
            </div>

            <div class="mt-4">
              <Pagination :collection="claims" />
            </div>
          </SubSection>

          <SubSection
            :title="'Achievements ' + unlockedAchievementsCount + '/' + achievementsCount"
          >
            <div class="grid grid-cols-3 items-start gap-4 md:grid-cols-5">
              <UserAchievement
                v-for="achievement in achievements"
                :key="achievement.id"
                :achievement="achievement"
              />
            </div>
          </SubSection>

          <slot name="default" />
        </slot>
      </div>
    </div>
  </Container>
</template>
